import { useIsMobile } from "@relay/rds/dist/index";
import maplibregl from "maplibre-gl";
import * as pmtiles from "pmtiles";
import { useEffect, useRef, useState } from "react";
import { formatTimeShort } from "../lib/formatters";
import { SocketRPC, socketEmit } from "../lib/socket";
import { useOrderHistory, useOrders } from "../store/orders";

function getMapBounds(order) {
  const bounds = new maplibregl.LngLatBounds();
  if (!order.isPickup) {
    bounds.extend([
      order.consumer.location.coordinates.longitude,
      order.consumer.location.coordinates.latitude,
    ]);
  }
  bounds.extend([
    order.producer.location.coordinates.longitude,
    order.producer.location.coordinates.latitude,
  ]);
  order.deliveries.forEach((delivery) => {
    if (delivery.riderPath) {
      delivery.riderPath.coordinates.forEach((point) =>
        bounds.extend([point.longitude, point.latitude])
      );
    } else if (delivery.lastPing?.latitude && delivery.lastPing?.longitude) {
      bounds.extend([delivery.lastPing.longitude, delivery.lastPing.latitude]);
    }
  });
  return bounds;
}

export default function TilesMap({
  lng = -73.993829,
  lat = 40.746229,
  zoom = 8,
  bboxPadding = { top: 65, bottom: 65, right: 50, left: 50 },
  orderKey,
  order,
  centerPanelRef,
}) {
  const mapContainer = useRef<any>();
  const map = useRef<any>(null);
  const { orders } = useOrders();
  const { orders: ordersHistory } = useOrderHistory();
  const isMobile = useIsMobile();

  if (!order) {
    order = orders[orderKey] || ordersHistory[orderKey];
  }

  const consumerMarkerElement = document.createElement("div");
  consumerMarkerElement.className = 'consumer-marker-container';

  const consumerMarkerETA = document.createElement('div');
  consumerMarkerETA.className = 'consumer-marker-eta hidden';
  consumerMarkerElement.appendChild(consumerMarkerETA);

  const consumerMarkerIcon = document.createElement('div');
  consumerMarkerIcon.className = 'consumer-marker';
  consumerMarkerElement.appendChild(consumerMarkerIcon);

  const consumerMarker = new maplibregl.Marker({
    element: consumerMarkerElement,
  });

  const producerMarkerElement = document.createElement("div");
  producerMarkerElement.className = "producer-marker";
  const producerMarker = new maplibregl.Marker({
    element: producerMarkerElement,
  });

  const riderMarkerElement = document.createElement("div");
  riderMarkerElement.className = "courier-marker color-0";

  const [riderMarkers, setRiderMarkers] = useState(
    () => [new maplibregl.Marker({element: riderMarkerElement})]
  );

  const PMTILES_URL = `${process.env.REACT_APP_RELAY_PMTILES_SERVER_URL}/world.pmtiles`;
  const STYLE_URL = `${process.env.REACT_APP_RELAY_PMTILES_SERVER_URL}/styles/arctic.json`;
  let protocol = new pmtiles.Protocol();
  maplibregl.addProtocol("pmtiles", protocol.tile);
  const p = new pmtiles.PMTiles(PMTILES_URL);

  protocol.add(p);

  useEffect(() => {
    if (!map.current) {
      map.current = new maplibregl.Map({
        container: mapContainer.current!,
        style: STYLE_URL,
        center: [
          order?.producer?.location?.coordinates?.longitude || lng,
          order?.producer?.location?.coordinates?.latitude || lat,
        ],
        zoom: zoom,
      });
    }
  }, [PMTILES_URL, zoom]);

  useEffect(() => {
    if (order.isPickup || !order.consumer.location?.coordinates) return;
    consumerMarker
      .setLngLat([
        order.consumer.location!.coordinates.longitude,
        order.consumer.location!.coordinates.latitude,
      ])
      .addTo(map.current);
  }, [order]);

  useEffect(() => {
    producerMarker
      .setLngLat([
        order.producer.location.coordinates.longitude,
        order.producer.location.coordinates.latitude,
      ])
      .addTo(map.current);
  }, [order.producer.location.coordinates]);

  useEffect(() => {
    if (order.isDelivered || order.status.order == 'Returned') {
      order.deliveries.forEach((delivery) => {
        if (delivery.riderPath) return;
        socketEmit(SocketRPC.GetJobFullPath, {
          orderKey: order.orderKey,
          deliveryKey: delivery.deliveryKey,
          jobKey: delivery.jobKey,
        });
      });
    } else {
      order.deliveries.forEach((delivery) => {
        if (delivery.riderPath) return;
        socketEmit(SocketRPC.GetJobRiderPings, {
          orderKey: order.orderKey,
          deliveryKey: delivery.deliveryKey,
          jobKey: delivery.jobKey,
        });
      });
    }
  }, [order]);

  useEffect(() => {
    order.deliveries.forEach((delivery, i) => {
      if ((delivery.isDelivered || delivery.isReturned) && delivery.riderPath) {
        if (riderMarkers[i]) riderMarkers[i].remove();
        delivery.riderPath.coordinates.forEach((riderLocation) => {
          const riderPathMarkerElement = document.createElement("div");
          riderPathMarkerElement.className = `courier-path-marker color-${i}`;
          new maplibregl.Marker({ element: riderPathMarkerElement })
            .setLngLat([riderLocation.longitude, riderLocation.latitude])
            .addTo(map.current);
        });
      } else if (delivery.lastPing) {
        if (!riderMarkers[i]) {
          const riderMarkerElement = document.createElement("div");
          riderMarkerElement.className = `courier-marker color-${i}`;

          riderMarkers[i] = new maplibregl.Marker({
            element: riderMarkerElement,
          });
          setRiderMarkers([...riderMarkers]);
        }
        riderMarkers[i]
          .setLngLat([delivery.lastPing.longitude, delivery.lastPing.latitude])
          .addTo(map.current);
      }
    });

    let myBboxPadding = { ...bboxPadding };
    if (mapContainer.current && centerPanelRef.current) {
      myBboxPadding.left =
        bboxPadding.left +
        centerPanelRef.current.getBoundingClientRect().left
        - mapContainer.current.getBoundingClientRect().left;
      myBboxPadding.right =
        bboxPadding.right +
        mapContainer.current.getBoundingClientRect().right -
        centerPanelRef.current.getBoundingClientRect().right;
      myBboxPadding.top =
        bboxPadding.top +
        mapContainer.current.getBoundingClientRect().top;
    }
    if (order.isPickup) {
      map.current
        .setCenter([
          order.producer.location.coordinates.longitude,
          order.producer.location.coordinates.latitude,
        ])
        .setZoom(15);
    } else {
      map.current.fitBounds(getMapBounds(order), {
        padding: isMobile ? 30 : myBboxPadding,
        linear: true,
      });
    }

    if (!order.consumer.location?.coordinates) {
      return;
    }

    let latestETA;
    if (!order.isDelivered) {
      const { deliveries = [] } = order;
      const estimatedArrivalTimes = deliveries
        .map((delivery) => ((delivery.estimatedTimeOfArrival && !delivery.riderMarkedAtConsumer) ? new Date(delivery.estimatedTimeOfArrival) : null))
        .filter(Boolean);
      latestETA = estimatedArrivalTimes.length > 0 ? Math.max(...estimatedArrivalTimes) : null;
    }

    if (latestETA) {
      const latestETATimestamp = new Date(latestETA);
      consumerMarkerETA.className = 'consumer-marker-eta';
      consumerMarkerETA.innerText = `ETA: ${formatTimeShort(latestETATimestamp.toISOString())}`;
    } else {
      if (consumerMarkerETA.className === 'consumer-marker-eta hidden') {
        return;
      }
      consumerMarkerETA.className = 'consumer-marker-eta hidden';
    }

    consumerMarker.setLngLat([
      order.consumer.location!.coordinates.longitude,
      order.consumer.location!.coordinates.latitude,
    ]).addTo(map.current);  

  }, [
    JSON.stringify(order.deliveries),
    centerPanelRef.current,
  ]);

  return <div ref={mapContainer} className="w-full h-full absolute" />;
}
